import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Zap, UserPlus, ArrowRight, ClipboardCheck, CalendarRange } from 'lucide-react';
import PoweredBy from '../components/ui/PoweredBy';

const PublicPortalLandingPage = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("Chargement...");
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/settings/public-config`)
        .then(res => res.json())
        .then(data => {
            setCompanyName(data.company_name);
            setCompanyLogo(data.logo_base64); 
        })
        .catch(() => setCompanyName("Mon Entreprise"));
  }, []);

  return (
    // FOND GLOBAL : Dégradé doux identique à la page de Booking
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 flex flex-col items-center justify-center p-6 font-sans text-slate-900">
      
      {/* BRANDING HEADER CENTRÉ */}
      <div className="max-w-3xl w-full text-center mb-12 animate-in fade-in slide-in-from-top-4 duration-700">
        <div className="flex justify-center items-center gap-4 mb-4">
          {companyLogo ? (
              <img src={companyLogo} alt="Logo Entreprise" className="h-16 w-auto object-contain drop-shadow-sm" />
          ) : (
              <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
                 <Zap className="w-7 h-7 text-white fill-white" />
              </div>
          )}
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">{companyName}</h1>
        </div>
        <p className="text-lg text-slate-600 max-w-xl mx-auto">
            Bienvenue sur votre espace dédié. Que souhaitez-vous faire aujourd'hui ?
        </p>
      </div>

      <div className="max-w-5xl w-full grid grid-cols-1 md:grid-cols-3 gap-6 animate-in zoom-in-95 duration-500">
        
        {/* OPTION 1 : RESERVATION (Ajout logique pour lier avec ta nouvelle page de booking) */}
        <Card 
          className="group cursor-pointer border-2 border-slate-200/60 hover:border-primary/50 transition-all hover:shadow-xl hover:-translate-y-1 bg-white text-slate-900 rounded-2xl flex flex-col"
          onClick={() => navigate('/booking')}
        >
          <CardHeader className="pb-4 flex-1">
            <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <CalendarRange className="w-7 h-7" />
            </div>
            <CardTitle className="text-xl font-bold text-slate-900">Prendre Rendez-vous</CardTitle>
            <CardDescription className="text-sm text-slate-600 mt-2">
              Consultez nos disponibilités et réservez vos prestations directement en ligne.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 mt-auto">
            <Button variant="secondary" className="w-full h-12 text-md font-medium group-hover:gap-3 transition-all bg-slate-100 text-slate-900 hover:bg-slate-200">
              Réserver <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* OPTION 2 : LEAD / PROJET */}
        <Card 
          className="group cursor-pointer border-2 border-slate-200/60 hover:border-primary/50 transition-all hover:shadow-xl hover:-translate-y-1 bg-white text-slate-900 rounded-2xl flex flex-col"
          onClick={() => navigate('/join')}
        >
          <CardHeader className="pb-4 flex-1">
            <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <ClipboardCheck className="w-7 h-7" />
            </div>
            <CardTitle className="text-xl font-bold text-slate-900">Nouveau Projet</CardTitle>
            <CardDescription className="text-sm text-slate-600 mt-2">
              Vous n'avez pas encore travaillé avec nous ? Demandez une étude personnalisée ou un devis.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 mt-auto">
            <Button className="w-full h-12 text-md font-medium group-hover:gap-3 transition-all shadow-md">
              Demander un devis <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* OPTION 3 : CLIENT / COMPTE */}
        <Card 
          className="group cursor-pointer border-2 border-slate-200/60 hover:border-primary/50 transition-all hover:shadow-xl hover:-translate-y-1 bg-white text-slate-900 rounded-2xl flex flex-col"
          onClick={() => navigate('/register')}
        >
          <CardHeader className="pb-4 flex-1">
            <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <UserPlus className="w-7 h-7" />
            </div>
            <CardTitle className="text-xl font-bold text-slate-900">Créer ma fiche Client</CardTitle>
            <CardDescription className="text-sm text-slate-600 mt-2">
              Vous avez déjà un accord avec nous ? Enregistrez vos coordonnées pour faciliter la facturation.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 mt-auto">
            <Button variant="secondary" className="w-full h-12 text-md font-medium group-hover:gap-3 transition-all bg-slate-100 text-slate-900 hover:bg-slate-200">
              S'enregistrer <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

      </div>

      <footer className="mt-16 text-slate-500 text-sm flex flex-col items-center gap-3">
        <p className="font-medium">© {new Date().getFullYear()} {companyName} • Portail Client & Partenaires</p>
        <div className="flex gap-4 text-slate-400">
          <a href="#" className="hover:underline hover:text-slate-600 transition-colors">Support</a>
          <span>•</span>
          <a href="#" className="hover:underline hover:text-slate-600 transition-colors">Mentions légales</a>
        </div>
        
        <div className="mt-4 opacity-80 hover:opacity-100 transition-opacity">
            <PoweredBy />
        </div>
      </footer>
    </div>
  );
};

export default PublicPortalLandingPage;